import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { Spinner } from 'components/UI';
import styles from './MonthlyBreakdownLine.module.scss';
import { MonthlyBreakdownReport } from 'model/BookingsMonthlyBreakdownReport';

interface Props {
    report: MonthlyBreakdownReport | null;
    loading: boolean;
}

/**
 * Paleta oficial por métrica
 */
const SERIES_COLORS = {
    totalBookings: '#5E8677',
    totalClients: '#92B4A7',
    totalPets: '#BAA997',
    totalServices: '#C18733',
    totalAddons: '#EAB464',
    totalProducts: '#B2B2B2',
    totalCustomItems: '#ECC915',
    totalCompleted: '#61CE8D',
    totalCanceled: '#6C6C6C',
    totalNoShows: '#000000'
} as const;

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MonthlyBreakdownLineChart: React.FC<Props> = ({ report, loading }) => {
    const data = useMemo(() => {
        if (!report) return [];

        return MONTH_KEYS.map((key, idx) => ({
            month: `${MONTH_LABELS[idx]} ${report.year}`,

            totalBookings: report.totalBookings[key],
            totalClients: report.totalClients[key],
            totalPets: report.totalPets[key],
            totalServices: report.totalServices[key],
            totalAddons: report.totalAddons[key],
            totalProducts: report.totalProducts[key],
            totalCustomItems: report.totalCustomItems[key],

            totalCompleted: report.completed.values[key]?.value ?? 0,
            totalCanceled: report.canceled.values[key]?.value ?? 0,
            totalNoShows: report.noShow.values[key]?.value ?? 0
        }));
    }, [report]);

    if (loading) {
        return (
            <div className={styles.loadingWrapper}>
                <Spinner />
            </div>
        );
    }

    if (!report) {
        return null;
    }

    return (
        <div className={styles.chartCard}>
            <h3 className={styles.title}>Monthly Breakdown</h3>

            <ResponsiveContainer
                width="100%"
                height={420}
            >
                <LineChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                >
                    <CartesianGrid
                        stroke="#eee"
                        vertical={false}
                    />

                    <XAxis
                        dataKey="month"
                        tick={{ fill: '#6b6b6b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <YAxis
                        tick={{ fill: '#6b6b6b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                    />

                    <Tooltip />

                    <Legend />

                    {/* === SERIES === */}

                    <Line
                        type="monotone"
                        dataKey="totalBookings"
                        name="Total Bookings"
                        stroke={SERIES_COLORS.totalBookings}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalClients"
                        name="Total Clients"
                        stroke={SERIES_COLORS.totalClients}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalPets"
                        name="Total Pets"
                        stroke={SERIES_COLORS.totalPets}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalServices"
                        name="Total Services"
                        stroke={SERIES_COLORS.totalServices}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalAddons"
                        name="Total Add-Ons"
                        stroke={SERIES_COLORS.totalAddons}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalProducts"
                        name="Total Products"
                        stroke={SERIES_COLORS.totalProducts}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalCustomItems"
                        name="Total Custom Items"
                        stroke={SERIES_COLORS.totalCustomItems}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalCompleted"
                        name="Total Completed"
                        stroke={SERIES_COLORS.totalCompleted}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalCanceled"
                        name="Total Canceled"
                        stroke={SERIES_COLORS.totalCanceled}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />

                    <Line
                        type="monotone"
                        dataKey="totalNoShows"
                        name="Total No Shows"
                        stroke={SERIES_COLORS.totalNoShows}
                        dot={{ r: 4 }}
                        strokeWidth={2}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MonthlyBreakdownLineChart;
